import type { TrustSealState } from './types';

/** Vanta-style trust center label: visitor knows what kind of page this is. */
export const TRUST_CENTER_EYEBROW = 'AI Trust Center';

/**
 * Below this 24h volume, raw check counts read as low-signal: show status,
 * not numbers. At or above it, the count is evidence and is displayed.
 */
export const CHECKS_24H_DISPLAY_FLOOR = 25;

/**
 * Enforcement seal status copy. Mirrors badge_svg.py badge_status_label.
 * The seal is region agnostic: it names the EU AI Act only when the tenant's
 * checks ran under jurisdiction EU, and the count beside it is EU checks only.
 * With no EU check in the window the law is not named.
 */
export const SEAL_STATUS_ENFORCED = 'EU AI Act · Enforced';
/** No EU check in the last 30 days: status without naming a law. */
export const SEAL_STATUS_ENFORCED_PLAIN = 'Enforced';
export const SEAL_STATUS_AWAITING = 'Awaiting activity';

export function sealStatusWithChecks(count: number): string {
  return `EU AI Act · ${count.toLocaleString('en-US')} checks`;
}

/** Count label when no EU check ran: the law is not named. */
export function plainStatusWithChecks(count: number): string {
  return `${count.toLocaleString('en-US')} checks`;
}

/**
 * Primary hero message, in the buyer's own words (2026-09 pain corpus): the
 * two things they ask for are proof the rules held and a block that held.
 * Matches the homepage hero. Concrete stakes, no "compliance" filler.
 * 2026-10-10: no "before the model" here; this page speaks for a third party's
 * product and cannot know whether it checks prompts, answers or both.
 */
export function trustHeroAssurance(state: TrustSealState, _namesEuAiAct: boolean): string {
  // 2026-10-30 (Leo: "isn't this too much text?"): three sentences. The articles
  // live in the first assurance card, the plan and review detail in the FAQ, so
  // the hero no longer names an article and reads the same for EU and US checks
  // (CE-REVIEW-TRUST-014 F1 still holds: no article is claimed here).
  if (state === 'active' || state === 'recent') {
    return 'Every check is logged. When a phrase rule or the injection check blocks a request, the rule and the law are named. This page is generated from that log, never from what users typed or what the AI answered.';
  }
  return "This product's AI runs behind ComplyEdge: a phrase-rule block carries the article it cites, and every check is logged. A wider-review block does not cite an article. No check in the last 30 days; live figures appear here once checks run.";
}

/** Optional activity line: check count lives here, only above the display floor. */
export function trustHeroActivityNote(state: TrustSealState, checks24h: number): string | null {
  if (state !== 'active' || checks24h < CHECKS_24H_DISPLAY_FLOOR) {
    return null;
  }
  // Same spelling as the seal and the count card under it: "1,234", never "1234"
  // (CE-REVIEW-TRUST-015 F1).
  return `${checks24h.toLocaleString('en-US')} checks logged in the last 24 hours.`;
}

/** Three assurance cards: same shape (one-line title, two short sentences, about
 *  20 words) so the grid reads as one row and the body text lines up. */
export const TRUST_ASSURANCE_ITEMS = [
  {
    title: 'Checked against the law',
    description:
      'Every check runs against legal rules: EU AI Act Article 5 prohibited practices on EU checks, US rules on US checks.',
  },
  {
    title: 'Logged outside the AI',
    description:
      'Each check writes one log entry outside the AI system. It is written as the request runs, not in a quarterly review.',
  },
  {
    title: 'A log you can hand over',
    description:
      'A phrase-rule block names the rule and the article it cites. A wider-review block does not. Customers and auditors ask for this.',
  },
] as const;

export const TRUST_STATUS_VISITOR_LABELS: Record<TrustSealState, string> = {
  active: 'Blocking and recording',
  recent: 'Blocking and recording · quiet 24h',
  idle: 'Connected: no checks in the last 30 days',
};

/** Status card detail. "Prohibited" names EU AI Act Article 5 practices, which
 *  run on EU checks only, so it follows the seal's EU rule
 *  (CE-REVIEW-TRUST-014 F1). */
export function trustStatusCardDetail(state: TrustSealState, namesEuAiAct: boolean): string {
  if (state === 'active') {
    return namesEuAiAct
      ? 'Prohibited requests blocked; every check logged'
      : 'Requests checked; every check logged';
  }
  return state === 'recent'
    ? 'Active · no checks in the last 24 hours'
    : 'No checks in the last 30 days';
}

/**
 * Laws and articles panel (2026-10-30, Leo: "combine these and make them look
 * good"): one panel, what fired first, the rule set's scope under it.
 */
export const LAWS_PANEL_TITLE = 'Laws and articles';
export const LAWS_PANEL_SUBTITLE =
  "From this product's own audit trail over the last 30 days, and the EU AI Act articles the rule set screens.";
/** The list is what FIRED, never every law checked against: an EU product with
 *  no hit lists nothing (CE-REVIEW-TRUST-011 F3). Only laws whose rules caught
 *  something on this product in the last 30 days (Leo, 2026-09-25). */
export const FRAMEWORKS_SECTION_TITLE = 'Laws that caught something';
export const FLAGSHIP_FRAMEWORK = 'EU AI Act';

/** Blocks only: a warning the product let through cites no article. */
export const ARTICLES_ENFORCED_LABEL = 'Blocked in the last 30 days';
/** Screening scope, not findings: the rule set's EU AI Act coverage, the same
 *  for every tenant (articles_screened comes from the corpus, not the tenant's
 *  traffic), and those rules match EU-jurisdiction checks only
 *  (CE-REVIEW-TRUST-003 F2). */
export const ARTICLES_SECTION_TITLE = 'EU AI Act articles screened';
export const ARTICLES_SECTION_SUBTITLE =
  'Articles the ComplyEdge rule set covers. They run on checks sent with an EU jurisdiction.';
/** Link that ends the articles coverage note: which rules, and the law behind
 *  each. These are the ComplyEdge rule set (open source, on GitHub), not the
 *  open rulebook: ADCR is the record format, kept separate on purpose. */
export const ARTICLES_RULE_SET_LINK_LABEL = 'Which rules, and the law behind each one';
/** Path on the marketing site; the host comes from getWebsiteUrl() (localhost when run locally). */
export const ARTICLES_RULE_SET_PATH = '/rule-set/';

/**
 * Conversion section: this page is where a visitor discovers ComplyEdge
 * (seal on a product → trust page → complyedge.io). One clear ask.
 * Sell the setup-cost contrast (weeks of trust-center work vs minutes).
 */
export const CTA_TITLE = 'See the log your AI would produce';
export const CTA_BODY =
  "ComplyEdge checks every AI call and logs each check, never what your users typed or what the AI answered. Link a page like this instead of filling in a questionnaire.";
export const CTA_PRIMARY_LABEL = 'Get ComplyEdge';
export const CTA_SECONDARY_LABEL = 'Embed this seal';

/**
 * Static FAQ: the one sanctioned Vanta-pattern addition (TP allowed_cheap_additions).
 * No infra, no hand-maintained dates.
 */
/** Collapsible block title, Vanta/GitLab trust-center convention. */
export const FAQ_SECTION_TITLE = 'FAQ';

/** Footer tagline and subline: same copy as the website footer partial
 *  (apps/website/templates/footer.html). */
export const FOOTER_TAGLINE = 'Blocks AI requests that break EU law.';
export const FOOTER_SUBLINE = "Every check is logged, never the prompt or the AI's answer.";

/** Full disclaimer: word-aligned with complyedge.io marketing site footer. */
export const FOOTER_DISCLAIMER =
  'Disclaimer: ComplyEdge provides compliance tooling, not legal advice. Always consult legal counsel for specific regulatory requirements.';
export const TRUST_FAQ_ITEMS = [
  {
    question: 'What is this page?',
    answer:
      "A live AI Trust Center. It shows how many of this product's AI decisions were checked, which laws a rule caught something under, and which EU AI Act articles the rule set covers: generated from runtime data, not uploaded documents.",
  },
  {
    question: 'What does "runtime enforced" mean?',
    answer:
      'The check runs while the AI operates, between the product and the AI model, on every call it sends. A phrase-rule block names the rule and the article it cites. The injection check can also block, and that block names the law. A wider-review block does not cite an article. If that review is on and clears every hit, the request is allowed. A review months later can only describe what happened; this log was written when it happened.',
  },
  {
    question: 'How is this different from a SOC 2 trust center?',
    answer:
      'Security trust centers host documents and self-reported questionnaire answers. This page is generated from live enforcement decisions: the status you see reflects checks that actually ran.',
  },
  {
    question: 'Who is ComplyEdge?',
    answer:
      'ComplyEdge checks every AI request and answer against an open rule set tied to the law, and logs every check outside your AI system. The rule and the law are named when a phrase rule or the injection check fired, never what your users typed or what the AI answered. It also edits ADCR, the open rulebook for what an AI decision record must prove, and builds its first implementation. ADCR is at version 0.1.0, in development; the gaps between ComplyEdge log entries and the rulebook are listed in its Annex C, and no conformance class is claimed. This AI Trust Center and the Enforcement Seal are part of the platform.',
  },
] as const;
